import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AnnotationSelectionChrome — the shared resize / rotate chrome
 * mounted on top of any selected (or otherwise actively-interactive)
 * annotation: drawing, sticker, stamp, emoji reaction.
 *
 * Lifted from `DrawingSelectionView` so every annotation type uses
 * one gesture stack. The chrome itself doesn't know anything about
 * specific annotation content — it works in an abstract `(scale,
 * rotation)` frame and emits the new values via callbacks. Each
 * caller maps those abstract patches back to type-specific fields:
 *
 *   - drawings  → `{ width, height }` (uniform scale applied to both)
 *   - stamps    → `{ scale }` (renderer scales font / padding /
 *                              border by `transform.height /
 *                              BASE_HEIGHT`)
 *   - stickers  → `{ scale }`
 *   - emoji     → `{ scale }` (same flow as stamps under the hood)
 *
 * Visual + cursor lock (drawing-cursors.ts) are unchanged from the
 * drawing's chrome — the only thing that moved is the API surface.
 *
 * `containerRef` is the host (the panel's annotation container) for
 * reading the live rect on every event. `centerX/Y` are in
 * container-normalized 0..1 coords; we resolve them to viewport
 * coords on every move so a mid-gesture container resize doesn't
 * drift the gesture's pivot.
 */
import { memo, useCallback, useEffect, useRef } from "react";
import { GESTURE_ACTIVE_BODY_CLASS, GESTURE_ACTIVE_VAR, buildResizeCursor, buildRotateCursor, } from "./views/drawing-cursors";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ROTATION_SNAP_DEG = 15;
// Floor on the chrome-emitted scale. Marks that own a `scale` prop
// can clamp further; this is a defense-in-depth zero guard so the
// caller never sees a 0 or negative scale (which would collapse the
// rendered annotation to a point). 0.05 matches the mark stack's
// historical SCALE_MIN — small enough to feel like "all the way
// down", large enough to avoid degenerate rendering.
const MIN_SCALE = 0.05;
const CORNERS = ["nw", "ne", "sw", "se"];
// ---------------------------------------------------------------------------
// Cursor lock helpers (lifted verbatim from DrawingSelectionView)
// ---------------------------------------------------------------------------
function beginGestureCursorLock(cursor) {
    document.body.classList.add(GESTURE_ACTIVE_BODY_CLASS);
    document.body.style.setProperty(GESTURE_ACTIVE_VAR, cursor);
}
function updateGestureCursor(cursor) {
    document.body.style.setProperty(GESTURE_ACTIVE_VAR, cursor);
}
function endGestureCursorLock() {
    document.body.classList.remove(GESTURE_ACTIVE_BODY_CLASS);
    document.body.style.removeProperty(GESTURE_ACTIVE_VAR);
}
function distanceFromCenter(centerClientX, centerClientY, clientX, clientY) {
    const dx = clientX - centerClientX;
    const dy = clientY - centerClientY;
    return Math.sqrt(dx * dx + dy * dy);
}
function angleFromCenterDeg(centerClientX, centerClientY, clientX, clientY) {
    const dx = clientX - centerClientX;
    const dy = clientY - centerClientY;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
}
function snapDeg(deg, step) {
    return Math.round(deg / step) * step;
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const AnnotationSelectionChrome = memo(function AnnotationSelectionChrome({ annotationId, containerRef, centerX, centerY, scale, rotation, onScale, onRotation, onSettle, }) {
    // Snapshot the gesture-start state so each move computes the
    // next value relative to the snapshot rather than walking deltas.
    // Deltas accumulate floating-point error on long drags; absolute
    // recomputation stays stable even across rapid pointer events.
    const resizeStartRef = useRef(null);
    const rotateStartRef = useRef(null);
    // Per-gesture dirty flags. Settle (which PATCHes the canonical
    // row to the server) only fires when the gesture actually moved.
    // A click-without-drag on a handle would otherwise emit a no-op
    // PATCH that races against any follow-up delete keystroke and
    // can resurrect the deleted row when the rollback runs.
    const resizeDirtyRef = useRef(false);
    const rotateDirtyRef = useRef(false);
    // Resolve the frame's center to client coords. Re-read on every
    // gesture move so a mid-gesture host resize (panel still finishing
    // a FLIP grow, viewport rotation, etc.) doesn't drift the pivot.
    const readCenter = useCallback(() => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0 || rect.height === 0)
            return null;
        return {
            cx: rect.left + centerX * rect.width,
            cy: rect.top + centerY * rect.height,
        };
    }, [containerRef, centerX, centerY]);
    // -- Resize gesture (corner squares) -------------------------------------
    const onResizePointerDown = useCallback((e, corner) => {
        if (e.pointerType === "mouse" && e.button !== 0)
            return;
        const center = readCenter();
        if (!center)
            return;
        const dist = distanceFromCenter(center.cx, center.cy, e.clientX, e.clientY);
        if (dist === 0)
            return;
        resizeStartRef.current = {
            initialDistance: dist,
            initialScale: scale,
        };
        resizeDirtyRef.current = false;
        e.currentTarget.setPointerCapture(e.pointerId);
        e.stopPropagation();
        // Lock the corner-specific resize cursor across the viewport
        // for the duration of the drag. Rotation doesn't change
        // during resize so the cursor stays fixed — no need to update
        // on each pointer-move.
        beginGestureCursorLock(buildResizeCursor(corner, rotation));
    }, [scale, rotation, readCenter]);
    const onResizePointerMove = useCallback((e) => {
        const start = resizeStartRef.current;
        if (!start)
            return;
        const center = readCenter();
        if (!center)
            return;
        const dist = distanceFromCenter(center.cx, center.cy, e.clientX, e.clientY);
        const ratio = dist / start.initialDistance;
        const next = Math.max(start.initialScale * ratio, MIN_SCALE);
        resizeDirtyRef.current = true;
        onScale(next);
    }, [readCenter, onScale]);
    const onResizePointerUp = useCallback((e) => {
        if (!resizeStartRef.current)
            return;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        resizeStartRef.current = null;
        endGestureCursorLock();
        const dirty = resizeDirtyRef.current;
        resizeDirtyRef.current = false;
        if (dirty) {
            onSettle();
        }
    }, [onSettle]);
    // -- Rotate gesture (corner outer hits) ----------------------------------
    const onRotatePointerDown = useCallback((e, corner) => {
        if (e.pointerType === "mouse" && e.button !== 0)
            return;
        const center = readCenter();
        if (!center)
            return;
        rotateStartRef.current = {
            initialAngleDeg: angleFromCenterDeg(center.cx, center.cy, e.clientX, e.clientY),
            initialRotation: rotation,
            corner,
        };
        rotateDirtyRef.current = false;
        e.currentTarget.setPointerCapture(e.pointerId);
        e.stopPropagation();
        beginGestureCursorLock(buildRotateCursor(corner, rotation));
    }, [rotation, readCenter]);
    const onRotatePointerMove = useCallback((e) => {
        const start = rotateStartRef.current;
        if (!start)
            return;
        const center = readCenter();
        if (!center)
            return;
        const currentAngle = angleFromCenterDeg(center.cx, center.cy, e.clientX, e.clientY);
        const next = start.initialRotation + (currentAngle - start.initialAngleDeg);
        // Free rotation by default; Shift snaps to 15°. Read shiftKey
        // off the live event so toggling Shift mid-drag responds
        // without needing to release & re-press.
        const snapped = e.shiftKey ? snapDeg(next, ROTATION_SNAP_DEG) : next;
        rotateDirtyRef.current = true;
        onRotation(snapped);
        // Cursor tracks the bbox's rotation in real time so the
        // arrowhead orientation always reads correctly relative to
        // the annotation.
        updateGestureCursor(buildRotateCursor(start.corner, snapped));
    }, [readCenter, onRotation]);
    const onRotatePointerUp = useCallback((e) => {
        if (!rotateStartRef.current)
            return;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        rotateStartRef.current = null;
        endGestureCursorLock();
        const dirty = rotateDirtyRef.current;
        rotateDirtyRef.current = false;
        if (dirty) {
            onSettle();
        }
    }, [onSettle]);
    // Defensive cleanup: if the chrome unmounts mid-gesture (selection
    // cleared via Escape, modal closed, parent tree torn down) the body
    // class + variable would otherwise stick around and leak the
    // gesture cursor across the rest of the app.
    useEffect(() => {
        return () => {
            endGestureCursorLock();
        };
    }, []);
    return (_jsxs("div", { className: "annotation-selection-chrome", "data-annotation-selection-for": annotationId, "aria-hidden": true, children: [_jsx("div", { className: "annotation-selection-chrome__outline", "aria-hidden": true }), CORNERS.map((corner) => (_jsx("div", { className: `annotation-selection-chrome__rotate annotation-selection-chrome__rotate--${corner}`, 
                // Cursor orientation tracks the bbox's CURRENT rotation so
                // a rotated annotation shows correctly-oriented cursors on
                // both hover and drag. Inline so each render with a new
                // rotation refreshes the cursor without a SCSS recompile.
                style: { cursor: buildRotateCursor(corner, rotation) }, onPointerDown: (e) => onRotatePointerDown(e, corner), onPointerMove: onRotatePointerMove, onPointerUp: onRotatePointerUp, onPointerCancel: onRotatePointerUp }, corner))), CORNERS.map((corner) => (_jsx("div", { className: `annotation-selection-chrome__handle annotation-selection-chrome__handle--${corner}`, 
                // Same dynamic-rotation rule as the rotate handles above.
                // A drawing rotated 45° shows diagonal-resize cursors that
                // align with its current (rotated) corners.
                style: { cursor: buildResizeCursor(corner, rotation) }, onPointerDown: (e) => onResizePointerDown(e, corner), onPointerMove: onResizePointerMove, onPointerUp: onResizePointerUp, onPointerCancel: onResizePointerUp }, corner)))] }));
});
